import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";
import { authService } from "@/services/authService";
import type { User } from "@/services/authService";

interface PasswordStepProps {
  userData: User | null;
  forcePwdAfterForgotIar: boolean;
  onSuccess: (wasForceRecovery: boolean) => void;
  onError: (msg: string) => void;
  onUserDataUpdate: (user: User) => void;
}

function validatePassword(pwd: string): string | null {
  if (pwd.length < 8) return "Password harus minimal 8 karakter";
  if (!/[A-Z]/.test(pwd)) return "Password harus mengandung minimal satu huruf besar";
  if (!/[a-z]/.test(pwd)) return "Password harus mengandung minimal satu huruf kecil";
  if (!/[0-9]/.test(pwd)) return "Password harus mengandung minimal satu angka";
  return null;
}

export function PasswordStep({ userData, forcePwdAfterForgotIar, onSuccess, onError, onUserDataUpdate }: PasswordStepProps) {
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (password !== confirmPassword) {
      setError("Password dan konfirmasi password tidak sama");
      return;
    }

    setIsLoading(true);
    try {
      const wasForceRecovery = forcePwdAfterForgotIar;
      const res = await apiFetch(`${apiConfig.baseURL}/api/auth/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      const data = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) {
        throw new Error(data.message || "Gagal menyimpan password");
      }

      try {
        sessionStorage.removeItem("orari_prompt_password_after_forgot_iar");
      } catch {
        /* ignore */
      }

      const u = await authService.getCurrentUser();
      if (u) onUserDataUpdate(u);

      setPassword("");
      setConfirmPassword("");

      onSuccess(wasForceRecovery);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Gagal menyimpan password";
      setError(msg);
      onError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {forcePwdAfterForgotIar && userData?.isPasswordSet === true && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-950">
          Anda masuk tanpa email terdaftar. Atur <strong>kata sandi baru</strong> agar lain kali bisa memakai
          opsi &quot;Lupa kata sandi&quot; setelah email ditambahkan, atau tetap gunakan IAR.
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="password">Password Baru</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Masukkan password baru"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Minimal 8 karakter, harus mengandung huruf besar, huruf kecil, dan angka
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Konfirmasi Password</Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Konfirmasi password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Menyimpan..." : "Lanjutkan"}
      </Button>
    </form>
  );
}
